import { IconButton } from '@nike/eds'
import { type ColumnFiltersState, type SortingState } from '@tanstack/react-table'
import { useFetchCountriesQuery } from 'api/country'
import { exportGradingInstructionsCsv, useCreateGradingInstructionsMutation, useDeleteGradingInstructionsMutation, useSearchGradingInstructionsQuery, useUpdateGradingInstructionsMutation } from 'api/instruction'
import { ConfirmationModal } from 'components/confirmation-modal'
import { ActionLocationType, TableView } from 'components/tableview'
import { ColumnType } from 'components/tableview/Filter'
import { usePaginatedData } from 'components/tableview/usePaginatedData'
import { useTableState } from 'components/tableview/useTableState'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { showSnackbar } from 'redux/slices/globalState'
import { dispatch, useAppSelector } from 'redux/store'
import { GradingInstruction, type QueryResult } from 'types'
import { cloneFilterCondition, type FilterCondition } from 'types/Instruction'

import { GradingInstructionEditor } from './components'
import { AuthRole } from 'auth/const'

const defaultSorting = [{ desc: true, id: 'lastModified' }]

const renderPills = (values: string[]) => {
  if (values.length === 0) {
    return <span className="italic text-gray-400">All</span>
  }
  return (
    <div className="flex flex-wrap gap-1">
      {values.map((v, i) => (
        <span key={i} className="inline-block px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">{v}</span>
      ))}
    </div>
  )
}

export const GradingInstructionOverview = () => {
  const user = useAppSelector((state) => state.globalState.user)

  const tableState = useTableState(defaultSorting)
  const columnFiltersTransformer = (filters: ColumnFiltersState): ColumnFiltersState => filters.map(filter => {
    if (filter.id === 'style') {
      return { id: 'productCode', value: filter.value }
    } else if (filter.id === 'description') {
      return { id: 'label', value: filter.value }
    }
    return filter
  })
  const sortingTransformer = (sorting: SortingState): SortingState => sorting.map(sort => {
    if (sort.id === 'style') {
      return { desc: sort.desc, id: 'productCode' }
    } else if (sort.id === 'description') {
      return { desc: sort.desc, id: 'label' }
    }
    return sort
  })
  const { data, loading } = usePaginatedData<QueryResult<GradingInstruction>>(useSearchGradingInstructionsQuery, tableState.pagination, tableState.columnFilters, tableState.sorting, undefined, columnFiltersTransformer, sortingTransformer)
  const FILTER_INPUT_DELAY_MS = 500

  const [createGradingInstructions, { isSuccess: createSuccessful, isLoading: creatingInstruction }] = useCreateGradingInstructionsMutation()
  const [updateGradingInstructions, { isSuccess: updateSuccessful, isLoading: updatingInstruction }] = useUpdateGradingInstructionsMutation()
  const [deleteGradingInstructions] = useDeleteGradingInstructionsMutation()
  const [selectedGradingInstruction, setSelectedGradingInstruction] = useState<GradingInstruction | null>(null)
  const [modal, setModal] = useState<'Create' | 'Edit'>('Create')
  const [toBeDeleted, setToBeDeleted] = useState<number | null>(null)
  const [exporting, setExporting] = useState(false)

  const columns = useMemo(() => [
    { id: 'style', header: 'Style', type: ColumnType.TEXT, columnWidth: 4, enableColumnFilter: true, enableSorting: true },
    { id: 'color', header: 'Color', type: ColumnType.TEXT, columnWidth: 4, enableColumnFilter: false, enableSorting: false },
    { id: 'description', header: 'Description', type: ColumnType.TEXT, columnWidth: 8, enableColumnFilter: true, enableSorting: true },
    { id: 'acceptableGrades', header: 'Allowed grades', type: ColumnType.ARRAY, columnWidth: 4, enableColumnFilter: false, enableSorting: false },
    { id: 'modifiedBy', header: 'Modified by', type: ColumnType.TEXT, columnWidth: 8, enableColumnFilter: true, enableSorting: true },
    { id: 'lastModified', header: 'Last Modified', type: ColumnType.DATE, columnWidth: 8, enableColumnFilter: false, enableSorting: true }
  ], [])

  const { data: countryOptions } = useFetchCountriesQuery()
  const countryCodeToName = useMemo(() => {
    const map = new Map<string, string>()
    ;(countryOptions ?? []).forEach(c => map.set(c.countryCode, c.fullName))
    return map
  }, [countryOptions])

  const renderRowExpansion = useCallback((row: GradingInstruction) => {
    const conditions: FilterCondition[] = row.conditions ?? []
    if (conditions.length === 0) {
      return (
        <span className="italic text-gray-500">Applies to all (no filter conditions)</span>
      )
    }
    return (
      <div className="flex flex-col gap-3">
        <div className="eds-type--title-6 text-gray-700">Filter conditions</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="py-1 pr-4 font-medium" style={{ width: '8%' }}>#</th>
              <th className="py-1 pr-4 font-medium" style={{ width: '28%' }}>Channels</th>
              <th className="py-1 pr-4 font-medium" style={{ width: '32%' }}>Ship Tos</th>
              <th className="py-1 font-medium" style={{ width: '32%' }}>Countries</th>
            </tr>
          </thead>
          <tbody>
            {conditions.map((c, idx) => (
              <tr key={idx} className="border-b border-gray-100 last:border-b-0 align-top">
                <td className="py-1 pr-4 text-gray-500">{idx + 1}</td>
                <td className="py-1 pr-4">{renderPills(c.channels)}</td>
                <td className="py-1 pr-4">{renderPills(c.shipTos)}</td>
                <td className="py-1">{renderPills(c.countries.map(code => {
                  const name = countryCodeToName.get(code)
                  return name ? `${code} \u2014 ${name}` : code
                }))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }, [countryCodeToName])

  const onSaveInstruction = (gradingInstruction: GradingInstruction) => {
    modal === 'Create' ? createGradingInstructions(gradingInstruction) : updateGradingInstructions(gradingInstruction)
  }

  const onCreate = () => {
    setModal('Create')
    setSelectedGradingInstruction(new GradingInstruction())
  }

  const onEdit = (row: GradingInstruction) => {
    setModal('Edit')
    setSelectedGradingInstruction(row)
  }

  const onDelete = (id: number) => {
    deleteGradingInstructions(id)
      .catch(() => {
        dispatch(showSnackbar({
          message: 'Failed to delete instruction',
          status: 'error'
        }))
      }).finally(() => {
        setToBeDeleted(null)
      })
  }

  const onCopy = (row: GradingInstruction) => {
    setModal('Create')
    const copy = new GradingInstruction()
    copy.description = row.description
    copy.instruction = row.instruction
    copy.acceptableGrades = row.acceptableGrades
    copy.conditions = row.conditions.map(cloneFilterCondition)
    setSelectedGradingInstruction(copy)
  }

  const onExport = async () => {
    setExporting(true)
    try {
      const params: Record<string, string> = {}
      const transformedFilters = columnFiltersTransformer(tableState.columnFilters)
      for (const filter of transformedFilters) {
        if (filter.value) params[filter.id] = String(filter.value)
      }
      const transformedSort = sortingTransformer(tableState.sorting)
      if (transformedSort.length > 0) {
        params.sortBy = transformedSort[0].id
        params.direction = transformedSort[0].desc ? 'DESC' : 'ASC'
      }
      await exportGradingInstructionsCsv(params)
    } catch {
      dispatch(showSnackbar({ message: 'Failed to export instructions', status: 'error' }))
    } finally {
      setExporting(false)
    }
  }

  useEffect(() => {
    if (createSuccessful || updateSuccessful) {
      setSelectedGradingInstruction(null)
    }
  }, [createSuccessful, updateSuccessful])

  const rowButtons = user?.hasSpecific(AuthRole.SPECIAL_INSTRUCTION)
    ? [
      {
        label: 'Edit',
        icon: 'Edit',
        hoverText: 'Edit',
        columnWidth: 4,
        onClick: (row: GradingInstruction) => { onEdit(row) }
      },
      {
        label: 'Delete',
        icon: 'Delete',
        hoverText: 'Delete',
        columnWidth: 4,
        onClick: (row: GradingInstruction) => { setToBeDeleted(row.id) }
      },
      {
        label: 'Copy',
        icon: 'CopyPaste',
        hoverText: 'Copy',
        columnWidth: 4,
        onClick: (row: GradingInstruction) => { onCopy(row) }
      }
    ]
    : [
      {
        label: 'View',
        icon: 'Show',
        hoverText: 'View Special Insctruction',
        columnWidth: 4,
        onClick: (row: GradingInstruction) => { onEdit(row) }
      }
    ]

  return (
    <>
      <div className="flex justify-end m-2 mb-[-0.8rem] gap-2">
        <IconButton
          icon="Download"
          onClick={onExport}
          label={''}
          title='Export Special Instructions to CSV'
          variant="secondary"
          size="medium"
          disabled={exporting}>
        </IconButton>
        {user?.hasSpecific(AuthRole.SPECIAL_INSTRUCTION) && (
          <IconButton
            icon="CreateRule"
            onClick={onCreate}
            label={''}
            title='Create new instruction'
            variant="secondary"
            size="medium">
          </IconButton>
        )}
      </div>
      <TableView
        columns={columns}
        data={data?.results ?? []}
        rowCount={data?.totalCount ?? 0}
        textFilterDelay={FILTER_INPUT_DELAY_MS}
        tableState={tableState}
        loading={loading}
        actions={rowButtons}
        actionLocation={ActionLocationType.LAST}
        actionsWidth={48}
        defaultSorting={defaultSorting}
        renderRowExpansion={renderRowExpansion}
      />
      {selectedGradingInstruction &&
        <GradingInstructionEditor
          gradingInstruction={selectedGradingInstruction}
          mode={modal}
          onSave={onSaveInstruction}
          onDismiss={() => { setSelectedGradingInstruction(null) }}
          onLoading={creatingInstruction || updatingInstruction} />
      }
      {toBeDeleted && <ConfirmationModal
        onTrigger={() => { onDelete(toBeDeleted) }}
        onCancel={() => { setToBeDeleted(null) }}
        title="Delete instruction"
        message="Are you sure you want to delete this instruction?"
        iconClass=""
        confirmText="Confirm"
      />}
    </>
  )
}
